import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  COMPLAINT_IMPORT_COLUMNS,
  COMPLAINT_IMPORT_REQUIRED_COLUMNS,
  buildComplaintImportTemplateCsv,
  type ComplaintImportResult,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { filesKeys } from './files-api';

export interface ImportRequest {
  file: File;
  localGovernmentId: string;
  assignedOfficerId?: string;
  dryRun: boolean;
}

/**
 * Upload a batch of complaints.
 *
 * The anchor and the dry-run flag travel as query parameters rather than in the
 * multipart body — the server's scope guard reads `localGovernmentId` before
 * the body has been parsed, so in the body it is invisible to the guard and the
 * upload is refused. See the endpoint's own comment.
 */
export async function importComplaints(request: ImportRequest): Promise<ComplaintImportResult> {
  const form = new FormData();
  form.append('file', request.file);

  const params = new URLSearchParams({
    localGovernmentId: request.localGovernmentId,
    dryRun: String(request.dryRun),
  });
  if (request.assignedOfficerId) params.set('assignedOfficerId', request.assignedOfficerId);

  const { data } = await api.post<ComplaintImportResult>(
    `/files/bulk-import?${params.toString()}`,
    form,
  );
  return data;
}

export function useImportComplaints() {
  const queryClient = useQueryClient();
  return useMutation({
    // The screen reports the failure itself.
    meta: { silent: true },
    mutationFn: importComplaints,
    onSuccess: (result) => {
      // A dry run wrote nothing, so the dashboard has not changed.
      if (!result.dryRun && result.created > 0) {
        void queryClient.invalidateQueries({ queryKey: filesKeys.all });
      }
    },
  });
}

/**
 * A starter CSV.
 *
 * The generator lives in the contract, beside the column list it is built from,
 * so the portal's download and the mobile app's share sheet cannot hand officers
 * two different templates.
 */
export const importTemplateCsv = buildComplaintImportTemplateCsv;

export { COMPLAINT_IMPORT_COLUMNS, COMPLAINT_IMPORT_REQUIRED_COLUMNS };
